import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import { PriceScale, PricingDiscount } from '../asset/assetApiTypes';
import type { EquipmentItem } from '../station/stationDefaultsApiTypes';

/** One bookable group from GET /admin/group on the new (ECS) backend — see
 *  new-backend/admin/src/controllers/groupController.ts. Used by the
 *  "Rental products" deposit surface and the voucher create group select. */
export interface AdminGroup {
    id: string;
    name: string | null;
    locationId: string | null;
    /** Raw compartment/use-case types of the group (e.g. 'sup'). Compare
     *  case-insensitively. */
    compartmentTypes: string[];
    /** Per-group security deposit; null = no deposit configured. */
    depositAmount: number | null;
    /**
     * The Product this Group mirrors (W1.2a). There are no Product routes yet,
     * so this list is the only way a caller can name one — the station wizard
     * links its objects to it.
     *
     * Sound because the topology is frozen 1:1 (`product.group_id` is unique,
     * #2229 keeps it mirrored on new writes): Group and Product are two names
     * for one row today. Null only for a Product created purely on the target
     * model and never linked, which nothing produces yet — such a row cannot be
     * linked to, so the picker filters it out rather than offering a dead entry.
     */
    productId: string | null;
    /** #3016: the manual position among the groups of its location; null =
     *  never placed. Absent from an older backend. */
    sortOrder?: number | null;
    /** #3016: the location's name, for the object order's location picker. */
    locationName?: string | null;
}

/**
 * Per-room member access (#1926) — GET/PUT /admin/group/:groupId/access.
 *
 * `organizationIds` empty means the room is PUBLIC. A room with grants is
 * closed to non-members only when `restricted` is true; with `restricted`
 * false its grants only carry coverage — everyone can book it, and the
 * organizations listed as covered book it without paying (ADR-0008 amendment
 * 2026-10-13). `membersChargeable` marks a room whose usage the space
 * agreement does not cover, so approved members go through the normal
 * external flow on it.
 */
export interface GroupAccess {
    groupId: string;
    organizationIds: string[];
    /**
     * Closed to non-members. Optional: a deployment before 2026-10-13 does not
     * send it, and then a room with grants is closed, as it was.
     */
    restricted?: boolean;
    /** Every assigned organization by name, and whether a contract covers it. */
    organizations?: { id: string, name: string, covered: boolean }[];
    membersChargeable: boolean;
    /**
     * The room's footing (#2154 item 7). `dedicated` means it IS the holding
     * organization's under their space agreement — rent, not capacity.
     *
     * Optional because an older deployment does not send it, and the screen
     * must degrade rather than assume: absent is NOT the same as `shared`, so
     * consumers gate on `accessMode === 'dedicated'` and stay quiet otherwise.
     */
    accessMode?: 'shared' | 'dedicated';
    /** The organization holding a dedicated room, for naming it on screen. */
    dedicatedTo?: string | null;
}

/** Body for PUT /admin/group/:groupId/access — the list replaces what is stored. */
/** Whether the room's allocation windows pause on holidays of the tenant's region (D7). */
export interface WindowPauseSettings {
    school: boolean;
    public: boolean;
}

export interface UpdateGroupAccessArgs {
    groupId: string;
    organizationIds: string[];
    membersChargeable: boolean;
}

/** Body for PATCH /admin/group/:groupId/deposit — null clears the deposit. */
export interface UpdateGroupDepositArgs {
    groupId: string;
    depositAmount: number | null;
}

// --- /admin/product — the Services product form (W1.2a target model) --------

/** `{ [lang]: { title?, description? } }` — the stored shape on both rows. */
export type ProductLangNames = Record<
    string,
    { title?: string, description?: string }
>;

/**
 * One product from GET /admin/product on the new (ECS) backend — see
 * new-backend/admin/src/controllers/productController.ts. The display fields
 * (name, description, photo, titles) are the PRODUCT's own, through the
 * shared rule `productDisplay` (`@kolula/shared`): a station Group's mirror
 * may fill a blank field, a room's Group never does, so a room product with
 * no photo of its own shows none rather than one of its rooms'.
 */
export interface AdminProduct {
    id: string;
    /** The mirror Group (1:1 topology freeze). Also the `?product=` id the
     *  pre-W1.2a deep-link contract sends. */
    groupId: string | null;
    name: string | null;
    description: string;
    featuredImage: string | null;
    depositAmount: number | null;
    langNames: ProductLangNames | null;
    /** Lowercased use case ('sup' | 'rental' | 'storage' | …). Compare
     *  case-insensitively anyway, per the API-values rule. */
    useCase: string | null;
    compartmentTypes: string[];
    /** #1353 VAT category id; null = unassigned. */
    taxCategoryId: string | null;
    /**
     * ADR-0026 product category; null = not filed under one.
     *
     * Unlike every other field here there is NO Group fallback behind it —
     * `Group` has no such column — so null means exactly "unassigned" and
     * never "the mirror has it instead".
     */
    productCategoryId: string | null;
    /** #3016: the manual position within its product category; null = never
     *  placed. Absent from an older backend. */
    sortOrder?: number | null;
    locationId: string | null;
    /** The live default Offer's price category — where the tariff editor
     *  writes this product's prices. */
    priceCategory: { id: string, name: string } | null;
    /** #2923: that category's live rows, whole scope included (class too) —
     *  read by the room product price dialog, whose save is replace-all. */
    priceScales: PriceScale[];
    /**
     * Option b (PO 2026-09-28): the room product's long-stay discounts, read
     * off its objects (stored per asset, identical by rule). The one value
     * when every object agrees ([] = no tiers); null when the objects
     * DIVERGE — then `discountSets` carries each distinct set and the dialog
     * makes the operator choose, never silently picks one.
     */
    pricingDiscounts: PricingDiscount[] | null;
    discountsDiverge: boolean;
    /** The largest guest capacity among the product's objects — for a room
     *  about to be divided, the whole room's. `null` when none carries one;
     *  absent from an older backend. */
    maxGuests?: number | null;
    discountSets: Array<{ discounts: PricingDiscount[], assetCount: number }>;
    /** Objects across all cells — 0 = no home for discount tiers yet. */
    objectCount: number;
    /**
     * D15 (product refactor 4.4): the product's DEFAULT per-booking limit;
     * null = no cap. A station may override it (station page). Absent from
     * an older backend.
     */
    maxLimit?: number | null;
    /** D15: the product's default equipment checklist; null = none. */
    equipment?: EquipmentItem[] | null;
}

/** Multipart args for POST /admin/product. */
export interface CreateProductArgs {
    nameDe: string;
    nameEn?: string;
    descriptionDe?: string;
    descriptionEn?: string;
    /** As entered ('' = none) — validated server-side by the shared rule. */
    depositAmount?: string;
    useCase: string;
    /** #1353 VAT category id; '' = unassigned. */
    taxCategoryId?: string;
    /**
     * ADR-0026: which product category this product is filed under; '' = none.
     * The backend refuses a category whose use case differs from the
     * product's, so the picker offers only matching ones.
     */
    productCategoryId?: string;
    featuredImage?: File | null;
    /** D15 default limit as entered: '' = none, else a whole number >= 1. */
    maxLimit?: string;
    /** D15 default checklist as a JSON list; '' clears it. */
    equipment?: string;
}

/** Multipart args for PATCH /admin/product/:productId — absent = unchanged. */
export interface UpdateProductArgs {
    productId: string;
    nameDe?: string;
    nameEn?: string;
    descriptionDe?: string;
    descriptionEn?: string;
    depositAmount?: string;
    /** #1353 VAT category id; '' = unassigned. Absent leaves it unchanged. */
    taxCategoryId?: string;
    /** ADR-0026 category id; '' clears it, absent leaves it unchanged. */
    productCategoryId?: string;
    featuredImage?: File | null;
    removeFeaturedImage?: boolean;
    /**
     * Option b: the room product's long-stay discount tiers as a JSON string
     * ('[]' clears them). Absent = unchanged. The backend writes the value to
     * every live asset of every cell.
     */
    pricingDiscounts?: string;
    /** D15 default limit: '' clears it, absent = unchanged. */
    maxLimit?: string;
    /** D15 default checklist as a JSON list: '' clears it, absent = unchanged. */
    equipment?: string;
}

/**
 * Why a product could not be retired. Mirrors `ProductRetireBlockReason` in
 * `new-backend/admin/src/lib/product/productRetire.ts`; each value has a
 * `services.products.retire.blocker_<reason>` key, and
 * `retireBlockerKeys.test.ts` pins the three lists together so a reason added
 * server-side cannot render as a raw key.
 */
export type ProductRetireBlocker =
    | 'has_linked_objects'
    | 'held_by_live_rental'
    | 'has_future_booking';

/** 200 body of POST /admin/product/:productId/retire. */
export interface RetireProductResult {
    retired: boolean;
    /** Present when `retired` is false — today only 'already_retired'. */
    reason?: string;
    cellsArchived?: number;
}

/** 409 body of the same endpoint — the refusal, with its counts. */
export interface RetireProductRefusal {
    blockers?: ProductRetireBlocker[];
    linkedObjectCount?: number;
    liveHolderCount?: number;
    futureBookingCount?: number;
}

/** 200 body of DELETE /admin/product/:productId. */
export interface DeleteProductResult {
    deleted: boolean;
    cellsDeleted?: number;
    priceListsDeleted?: number;
    imagesDeleted?: number;
}

// --- GET /admin/group/pricing-structure ------------------------------------
// Read-only price view for the Services area (service-configuration-area
// plan, phase 1b). Mirrors getGroupPricingStructure in
// new-backend/admin/src/controllers/groupController.ts.

/** One weekday's opening window, in the stored hour convention. */
export interface GroupOpeningHour {
    weekday: string;
    fromHour: number;
    toHour: number;
}

/** A slot grid or structural gap in a group's price table — ADR-0018 rule 3:
 *  a slot no row covers is not bookable, so every hole is unbookable time. */
export interface PriceCoverageHole {
    scope:
        | 'no_rows'
        | 'no_duration_tier'
        | 'missing_nightly'
        | 'missing_weekday_rate'
        | 'missing_weekend_rate'
        | 'sup_slot';
    weekday?: string;
    fromHour?: number;
    toHour?: number;
    durationMinutes?: number;
}

/** Verdict of probing the LIVE price matcher against the opening hours.
 *  `not_checked` is its own state — a missing check must never render as a
 *  passing one. */
export interface PriceCoverage {
    kind: 'ok' | 'holes' | 'no_rows' | 'not_checked';
    holes: PriceCoverageHole[];
}

/** A group's price structure: category, rows, opening hours, and one coverage
 *  verdict per use case the group carries. `priceScales` has the same runtime
 *  shape as the asset detail's rows, so `PriceScaleTable` renders both. */
export interface GroupPricingStructure {
    id: string;
    name: string | null;
    /** The Group's own description; for a room, the room's. Absent on an older server. */
    description?: string;
    /** The Group's own photo; for a room, the room's. Absent on an older server. */
    featuredImage?: string | null;
    locationId: string | null;
    locationName: string | null;
    /** #3219: the group location's IANA zone; absent on an older server. */
    timeZone?: string;
    compartmentTypes: string[];
    /** W1.2a mirror: the group's Product, for `?product=` deep links. Null for
     *  groups created after the backfill that have no Product yet. */
    productId: string | null;
    priceCategory: { id: string, name: string } | null;
    /** Slice 2: which link priced this cell — its own location sheet, the
     *  product's default Offer, or the pre-W1.2a legacy fallback. */
    rateOrigin: 'product' | 'product_location' | 'legacy_group';
    /** The effective Offer's id; the location-sheet remove action needs it. */
    offerId: string | null;
    priceScales: PriceScale[];
    openingHours: GroupOpeningHour[];
    /** Keyed by lowercased use case. */
    coverage: Record<string, PriceCoverage>;
}

export interface GroupPricingStructureResponse {
    groups: GroupPricingStructure[];
}

/** The multipart body both product mutations send — absent keys stay absent,
 *  so a PATCH only carries what changed (the backend treats absence as
 *  "unchanged" and '' as "clear"). */
const buildProductFormData = (args: {
    nameDe?: string;
    nameEn?: string;
    descriptionDe?: string;
    descriptionEn?: string;
    depositAmount?: string;
    useCase?: string;
    taxCategoryId?: string;
    productCategoryId?: string;
    featuredImage?: File | null;
    removeFeaturedImage?: boolean;
    pricingDiscounts?: string;
    maxLimit?: string;
    equipment?: string;
}): FormData => {
    const formData = new FormData();
    if (args.nameDe !== undefined) formData.append('nameDe', args.nameDe);
    if (args.nameEn !== undefined) formData.append('nameEn', args.nameEn);
    if (args.descriptionDe !== undefined) formData.append('descriptionDe', args.descriptionDe);
    if (args.descriptionEn !== undefined) formData.append('descriptionEn', args.descriptionEn);
    if (args.depositAmount !== undefined) formData.append('depositAmount', args.depositAmount);
    if (args.useCase !== undefined) formData.append('useCase', args.useCase);
    // #1353. '' clears the assignment, which makes the offering unconfigured
    // and the VAT breakdown withheld — never a silent fallback rate.
    if (args.taxCategoryId !== undefined) {
        formData.append('taxCategoryId', args.taxCategoryId);
    }
    // ADR-0026. Same absent-vs-empty distinction as above: '' clears the
    // category, an absent field leaves it alone. Collapsing the two would
    // unfile a product on every save that did not touch the picker.
    if (args.productCategoryId !== undefined) {
        formData.append('productCategoryId', args.productCategoryId);
    }
    if (args.featuredImage) formData.append('featuredImage', args.featuredImage);
    if (args.removeFeaturedImage) formData.append('removeFeaturedImage', 'true');
    // Option b: absent = unchanged, '[]' clears — same absent-vs-empty
    // distinction as the category assignment above.
    if (args.pricingDiscounts !== undefined) {
        formData.append('pricingDiscounts', args.pricingDiscounts);
    }
    // D15: the product's defaults, never its stations' (absent = unchanged).
    if (args.maxLimit !== undefined) formData.append('maxLimit', args.maxLimit);
    if (args.equipment !== undefined) formData.append('equipment', args.equipment);
    return formData;
};

// Shared admin group endpoints on the new (ECS) backend. One slice = one base
// query: the legacy `assetGroupApiSlice` targets the old Lambda backend and
// stays untouched (docs/plans/deposit-admin-group-config.md).
export const groupAdminApiSlice = createApi({
    reducerPath: 'groupAdminApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['AdminGroups', 'GroupAccess', 'Products', 'PricingStructure', 'WindowPause'],
    endpoints: (builder) => ({
        // Non-archived groups with a location (voucher requirement, #1189).
        getAdminGroups: builder.query<AdminGroup[], void>({
            query: () => ({
                url: '/group',
                method: 'GET',
            }),
            providesTags: ['AdminGroups'],
        }),
        // Set / change / clear a group's deposit. Invalidates the list so the
        // "Rental products" table refetches the persisted value.
        updateGroupDeposit: builder.mutation<AdminGroup, UpdateGroupDepositArgs>({
            query: ({ groupId, depositAmount }) => ({
                url: `/group/${encodeURIComponent(groupId)}/deposit`,
                method: 'PATCH',
                data: { depositAmount },
            }),
            // 'Products' too: the endpoint dual-writes the mirrored
            // product.deposit_amount (W1.2a), which the product list shows.
            invalidatesTags: ['AdminGroups', 'Products'],
        }),
        // Read-only price structure for the Services area. The tag covers
        // ONLY what this slice's own mutations change (product create/edit —
        // a new product's group must appear here for its prices to be
        // editable). Price and opening-hours edits happen through the
        // asset-detail slices, which cannot invalidate tags across slices, so
        // consumers still refetch on mount (see ServicesPricingSection).
        getGroupPricingStructure: builder.query<GroupPricingStructureResponse, void>({
            query: () => ({
                url: '/group/pricing-structure',
                method: 'GET',
            }),
            providesTags: ['PricingStructure'],
        }),
        // Products for the Services form and the station wizard's link step.
        // Unlike getAdminGroups this includes products without a location —
        // a Services-created product has none until objects are linked.
        getProducts: builder.query<AdminProduct[], void>({
            query: () => ({
                url: '/product',
                method: 'GET',
            }),
            providesTags: ['Products'],
        }),
        // Pricing stream slice 1: point an object's Group cell at an existing
        // Product (PATCH /admin/group/:groupId/product). The asset detail's
        // own refetch is triggered by the updateAsset call that follows in
        // the same save (its tag lives in assetApiSlice); these tags refresh
        // the Services-side product and pricing views.
        updateGroupProduct: builder.mutation<
            { changed: boolean, productId: string, orphanArchived?: boolean },
            { groupId: string, productId: string }
        >({
            query: ({ groupId, productId }) => ({
                url: `/group/${groupId}/product`,
                method: 'PATCH',
                data: { productId },
            }),
            invalidatesTags: ['Products', 'AdminGroups', 'PricingStructure'],
        }),
        // Slice 2 (ADR-0025 amendment 2026-09-28): location sheets — a second,
        // location-scoped Offer per (product, location), created as a copy of
        // the default list and archived back to it.
        createLocationOffer: builder.mutation<
            { offerId: string, priceCategoryId: string, copiedRows: number },
            { productId: string, locationId: string }
        >({
            query: ({ productId, locationId }) => ({
                url: `/product/${productId}/offers`,
                method: 'POST',
                data: { locationId },
            }),
            invalidatesTags: ['Products', 'AdminGroups', 'PricingStructure'],
        }),
        archiveLocationOffer: builder.mutation<
            { archived: boolean },
            { productId: string, offerId: string }
        >({
            query: ({ productId, offerId }) => ({
                url: `/product/${productId}/offers/${offerId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Products', 'AdminGroups', 'PricingStructure'],
        }),
        createProduct: builder.mutation<AdminProduct, CreateProductArgs>({
            query: (args) => ({
                url: '/product',
                method: 'POST',
                data: buildProductFormData(args),
            }),
            invalidatesTags: ['Products', 'AdminGroups', 'PricingStructure'],
        }),
        updateProduct: builder.mutation<AdminProduct, UpdateProductArgs>({
            query: ({ productId, ...args }) => ({
                url: `/product/${encodeURIComponent(productId)}`,
                method: 'PATCH',
                data: buildProductFormData(args),
            }),
            invalidatesTags: ['Products', 'AdminGroups', 'PricingStructure'],
        }),
        /**
         * Retire a product — archive it and every Group cell it has.
         *
         * Invalidates the same three tags as create/edit, because that is what
         * the write touches: the product list ('Products'), the group list the
         * tariff editor and voucher selects read ('AdminGroups'), and the
         * price view keyed on those groups ('PricingStructure'). A retire that
         * left any of them stale would show the product as still on offer,
         * which is the one thing the operator just said it is not.
         */
        retireProduct: builder.mutation<RetireProductResult, { productId: string }>({
            query: ({ productId }) => ({
                url: `/product/${encodeURIComponent(productId)}/retire`,
                method: 'POST',
            }),
            invalidatesTags: ['Products', 'AdminGroups', 'PricingStructure'],
        }),
        /**
         * The master-only hard delete (#2636) — same invalidation set as the
         * retire above, for the same reason: a deleted product left in any of
         * those caches would still read as on offer.
         */
        deleteProduct: builder.mutation<DeleteProductResult, { productId: string }>({
            query: ({ productId }) => ({
                url: `/product/${encodeURIComponent(productId)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Products', 'AdminGroups', 'PricingStructure'],
        }),
        getGroupAccess: builder.query<GroupAccess, { groupId: string }>({
            query: ({ groupId }) => ({
                url: `/group/${encodeURIComponent(groupId)}/access`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { groupId }) => [
                { type: 'GroupAccess' as const, id: groupId },
            ],
        }),
        updateGroupAccess: builder.mutation<GroupAccess, UpdateGroupAccessArgs>({
            query: ({ groupId, organizationIds, membersChargeable }) => ({
                url: `/group/${encodeURIComponent(groupId)}/access`,
                method: 'PUT',
                data: { organizationIds, membersChargeable },
            }),
            invalidatesTags: (_result, _error, { groupId }) => [
                { type: 'GroupAccess' as const, id: groupId },
            ],
        }),
        getWindowPause: builder.query<WindowPauseSettings, { groupId: string }>({
            query: ({ groupId }) => ({
                url: `/group/${encodeURIComponent(groupId)}/window-pause`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { groupId }) => [
                { type: 'WindowPause' as const, id: groupId },
            ],
        }),
        updateWindowPause: builder.mutation<WindowPauseSettings, { groupId: string } & WindowPauseSettings>({
            query: ({ groupId, school, public: publicHolidays }) => ({
                url: `/group/${encodeURIComponent(groupId)}/window-pause`,
                method: 'PATCH',
                data: { school, public: publicHolidays },
            }),
            invalidatesTags: (_result, _error, { groupId }) => [
                { type: 'WindowPause' as const, id: groupId },
            ],
        }),
        /** #3016: the order of one location's rooms/objects as the booking
         *  page lists them — every live group of the location, in order. */
        reorderGroups: builder.mutation<void, { locationId: string, ids: string[] }>({
            query: (data) => ({ url: '/group/order', method: 'PUT', data }),
            invalidatesTags: ['AdminGroups', 'PricingStructure'],
        }),
        /** #3016: the order of the products within one product category. */
        reorderProducts: builder.mutation<void, { categoryId: string, ids: string[] }>({
            query: (data) => ({ url: '/product/order', method: 'PUT', data }),
            invalidatesTags: ['Products'],
        }),
    }),
});

export const {
    useGetAdminGroupsQuery,
    useGetGroupPricingStructureQuery,
    useUpdateGroupDepositMutation,
    useGetGroupAccessQuery,
    useUpdateGroupAccessMutation,
    useGetWindowPauseQuery,
    useUpdateWindowPauseMutation,
    useGetProductsQuery,
    useUpdateGroupProductMutation,
    useCreateLocationOfferMutation,
    useArchiveLocationOfferMutation,
    useCreateProductMutation,
    useUpdateProductMutation,
    useRetireProductMutation,
    useDeleteProductMutation,
    useReorderGroupsMutation,
    useReorderProductsMutation,
} = groupAdminApiSlice;
